import {Directive, inject, Input} from '@angular/core';
import {ButtonAppConfig, ButtonFocusRingPosition, HtmlButtonType, RouteNameCapableButtonComponent} from '@aviary/buttons/button.types';
import {BUTTON_APP_CONFIG} from '@aviary/injection-tokens/app-provided.tokens';
import {Params} from '@angular/router';

@Directive({
  host: {
    '[class.mx-focus-ring-inside]': `focusRingPosition === 'inside'`,
    '[class.mx-focus-ring-outside]': `focusRingPosition === 'outside'`,
    '[class.mx-disabled]': '!enabled'
  }
})
export abstract class BaseButtonComponent implements RouteNameCapableButtonComponent {
  protected readonly config: ButtonAppConfig = inject(BUTTON_APP_CONFIG);

  @Input() public type: HtmlButtonType = 'button';
  @Input() public enabled: boolean = true;
  @Input() public tooltip: string = '';
  @Input() public disabledTooltip: string = '';

  /** External URL. Links open in a new tab with noopener/noreferrer by default. */
  @Input() public href?: string;
  @Input() public openInNewTab: boolean = true;

  /** These are ignored unless an app-specific [routeName] directive is applied. */
  @Input() public routeParams: Params = {};
  @Input() public queryParams?: Params;

  /** This is set when either an app-specific [routeName] directive or Aviary's internal [aviaryRouteName] directive is applied. Do not set manually. */
  public genericRouteName?: string;

  public readonly focusRingPosition: ButtonFocusRingPosition = this.config.focusRingPosition;

  public get externalLinkTarget(): '_blank' | null {
    return this.openInNewTab ? '_blank' : null;
  }

  public get externalLinkRelationship(): 'noopener noreferrer' | null {
    return this.openInNewTab ? 'noopener noreferrer' : null;
  }

  public get computedTooltip(): string {
    return this.enabled || !this.disabledTooltip ? this.tooltip : this.disabledTooltip;
  }

  public get computedHref(): string | null {
    return this.enabled ? (this.href ?? null) : null;
  }

  public get computedAriaDisabled(): true | null {
    return !this.enabled || null;
  }

  public get computedTabIndex(): -1 | null {
    return this.enabled ? null : -1;
  }

  public onClick(event: MouseEvent): void {
    if (!this.enabled) event.stopPropagation();
  }
}
